// components/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import CreateDeploymentModal from './CreateDeploymentModal';
import DeploymentsView from './DeploymentsView';

type ViewMode = 'dashboard' | 'deployments';

const Dashboard: React.FC = () => {
  const { logout, deployments, checkAuthStatus } = useAuth();
  const [currentView, setCurrentView] = useState<ViewMode>('dashboard');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  // Check authentication status when dashboard loads
  useEffect(() => {
    if (!checkAuthStatus()) {
      console.log('Dashboard: Auth check failed, redirecting to login');
      logout();
    }
  }, [checkAuthStatus, logout]);

  // Periodic auth check while dashboard is active
  useEffect(() => {
    const interval = setInterval(() => {
      if (!checkAuthStatus()) {
        console.log('Dashboard: Session expired, logging out');
        logout();
      }
    }, 2 * 60 * 1000); // Check every 2 minutes

    return () => clearInterval(interval);
  }, [checkAuthStatus, logout]);

  const handleCreateDeployment = () => {
    setShowCreateModal(true);
  };

  const handleViewDeployments = () => {
    setCurrentView('deployments');
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
  };

  if (currentView === 'deployments') {
    return <DeploymentsView onBack={handleBackToDashboard} />;
  }

  return (
    <div style={{
      height: "100vh",
      background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)",
      display: "flex",
      flexDirection: "column",
      padding: '0',
      fontFamily: "Inter, sans-serif",
      position: 'relative',
      overflow: 'hidden'
    }}>

      {/* Header with logout */}
      <div style={{ 
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
        padding: '12px 32px',
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        flexShrink: 0
      }}>
        <div>
          <h1 style={{ color: '#1e293b', fontSize: '26px', fontWeight: 900, margin: 0 }}>PioneData Deployer</h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: '2px 0 0 0' }}>Deploy your HTML sites instantly with one click</p>
        </div>
        <button 
          onClick={logout}
          style={{
            padding: '10px 20px',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontWeight: 700,
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(220, 38, 38, 0.3)';
          }}
        >
          Logout
        </button>
      </div>

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2vh 4vw',
        textAlign: 'center',
        color: '#1e293b',
        overflow: 'hidden',
        minHeight: 0
      }}>
        
        {/* Hero Section */}
        <div style={{ marginBottom: '3vh', flexShrink: 0 }}>
          <h2 style={{
            fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
            fontWeight: 800,
            margin: '0 0 1vh 0',
            background: 'linear-gradient(135deg, #1e293b 0%, #3b82f6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Welcome to Your Dashboard
          </h2>
          <p style={{
            fontSize: 'clamp(1.1rem, 1.5vw, 1.4rem)',
            color: '#64748b',
            margin: '0 auto 3vh auto',
            maxWidth: '600px',
            lineHeight: '1.5',
            textAlign: 'center'
          }}>
            Manage your deployments with ease. Create new sites or view your existing deployments.
          </p>
          
          {/* Quick Stats */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'clamp(1rem, 2vw, 2rem)',
            flexWrap: 'wrap',
            marginBottom: '4vh',
            flexShrink: 0
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: 'clamp(12px, 1.5vh, 18px) clamp(16px, 2vw, 24px)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              textAlign: 'center',
              minWidth: 'clamp(140px, 12vw, 180px)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ fontSize: 'clamp(1.8rem, 2.5vw, 2.2rem)', fontWeight: 800, marginBottom: '0.5vh', color: '#3b82f6' }}>
                {deployments.length}
              </div>
              <div style={{ fontSize: 'clamp(0.8rem, 1vw, 1rem)', color: '#64748b', fontWeight: 600 }}>
                Total Deployments
              </div>
            </div>
            
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: 'clamp(12px, 1.5vh, 18px) clamp(16px, 2vw, 24px)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              textAlign: 'center',
              minWidth: 'clamp(140px, 12vw, 180px)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ fontSize: 'clamp(1.8rem, 2.5vw, 2.2rem)', fontWeight: 800, marginBottom: '0.5vh', color: '#10b981' }}>
                {deployments.length}
              </div>
              <div style={{ fontSize: 'clamp(0.8rem, 1vw, 1rem)', color: '#64748b', fontWeight: 600 }}>
                Active Sites
              </div>
            </div>
            
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: 'clamp(12px, 1.5vh, 18px) clamp(16px, 2vw, 24px)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              textAlign: 'center',
              minWidth: 'clamp(140px, 12vw, 180px)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ fontSize: 'clamp(1.8rem, 2.5vw, 2.2rem)', fontWeight: 800, marginBottom: '0.5vh', color: '#f59e0b' }}>
                {deployments.length > 0 ? new Date(deployments[0].created_at).getDate() : '0'}
              </div>
              <div style={{ fontSize: 'clamp(0.8rem, 1vw, 1rem)', color: '#64748b', fontWeight: 600 }}>
                Latest Deploy
              </div>
            </div>
          </div>
        </div>

        {/* Action Cards */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: 'clamp(1.5rem, 3vw, 3rem)',
          flexWrap: 'wrap',
          width: '100%',
          maxWidth: '1000px',
          flexShrink: 0
        }}>

        {/* Create New Deployment Card */}
        <div 
          onClick={handleCreateDeployment}
          style={{
            width: 'clamp(300px, 25vw, 380px)',
            height: 'clamp(200px, 20vh, 250px)',
            background: "white",
            borderRadius: '20px',
            padding: 'clamp(20px, 2vh, 30px) clamp(18px, 2vw, 28px)',
            cursor: 'pointer',
            boxShadow: "0 8px 25px rgba(59, 130, 246, 0.15)",
            border: "2px solid #e2e8f0",
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            transform: 'translateY(0)',
            position: 'relative'
          }}
          onMouseEnter={(e) => {
            const target = e.currentTarget as HTMLElement;
            target.style.transform = 'translateY(-4px)';
            target.style.boxShadow = '0 12px 35px rgba(59, 130, 246, 0.2)';
            target.style.borderColor = '#3b82f6';
          }}
          onMouseLeave={(e) => {
            const target = e.currentTarget as HTMLElement;
            target.style.transform = 'translateY(0)';
            target.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.15)';
            target.style.borderColor = '#e2e8f0';
          }}
        >
          <div style={{
            fontSize: 'clamp(3rem, 5vw, 4rem)',
            marginBottom: 'clamp(12px, 1.5vh, 20px)',
            filter: 'drop-shadow(0 4px 12px rgba(59, 130, 246, 0.3))'
          }}>
            🚀
          </div>
          <h3 style={{
            color: '#1e293b',
            fontSize: 'clamp(1.2rem, 1.8vw, 1.6rem)',
            fontWeight: 800,
            margin: '0 0 clamp(8px, 1vh, 12px) 0',
            letterSpacing: '-0.3px'
          }}>
            Create New Deployment
          </h3>
          <p style={{
            color: '#64748b',
            fontSize: 'clamp(0.9rem, 1.2vw, 1.1rem)',
            margin: 0,
            lineHeight: '1.4',
            fontWeight: 500
          }}>
            Upload your HTML file and deploy instantly
          </p>
        </div>

        {/* View Deployments Card */}
        <div 
          onClick={handleViewDeployments}
          style={{
            width: 'clamp(300px, 25vw, 380px)',
            height: 'clamp(200px, 20vh, 250px)',
            background: "white",
            borderRadius: '20px',
            padding: 'clamp(20px, 2vh, 30px) clamp(18px, 2vw, 28px)',
            cursor: 'pointer',
            boxShadow: "0 8px 25px rgba(16, 185, 129, 0.15)",
            border: "2px solid #e2e8f0",
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            transform: 'translateY(0)',
            position: 'relative'
          }}
          onMouseEnter={(e) => {
            const target = e.currentTarget as HTMLElement;
            target.style.transform = 'translateY(-4px)';
            target.style.boxShadow = '0 12px 35px rgba(16, 185, 129, 0.2)';
            target.style.borderColor = '#10b981';
          }}
          onMouseLeave={(e) => {
            const target = e.currentTarget as HTMLElement;
            target.style.transform = 'translateY(0)';
            target.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.15)';
            target.style.borderColor = '#e2e8f0';
          }}
        >
          <div style={{
            fontSize: 'clamp(3rem, 5vw, 4rem)',
            marginBottom: 'clamp(12px, 1.5vh, 20px)',
            filter: 'drop-shadow(0 4px 12px rgba(16, 185, 129, 0.3))'
          }}>
            📊
          </div>
          <h3 style={{
            color: '#1e293b',
            fontSize: 'clamp(1.2rem, 1.8vw, 1.6rem)',
            fontWeight: 800,
            margin: '0 0 clamp(8px, 1vh, 12px) 0',
            letterSpacing: '-0.3px'
          }}>
            View Your Deployments
          </h3>
          <p style={{
            color: '#64748b',
            fontSize: 'clamp(0.9rem, 1.2vw, 1.1rem)',
            margin: '0 0 clamp(8px, 1vh, 12px) 0',
            lineHeight: '1.4',
            fontWeight: 500
          }}>
            See all deployed sites and history
          </p>
          <div style={{
            padding: '6px 14px',
            background: '#f1f5f9',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: 700,
            color: '#475569',
            border: '1px solid #e2e8f0'
          }}>
            {deployments.length} deployment{deployments.length !== 1 ? 's' : ''}
          </div>
        </div>
        </div>
      </div>
      
      {/* Create Deployment Modal */}
      {showCreateModal && (
        <CreateDeploymentModal onClose={handleModalClose} />
      )}
    </div>
  );
};

export default Dashboard;